// src/containers/global/Web3AuthProvider.tsx
//
// The Web3Auth session lifecycle lives in @filedgr/web-core/auth — the exact
// same provider filedgr-web-app uses. This file keeps only Kuebefarm's
// env/brand/chain/login-method configuration and hands it to the shared
// provider via `buildOptions`. Consumers still import `useWeb3Auth` and
// `<Web3AuthProvider>` from here unchanged.
//
// SPEC §4 (LOCKED): the client ID, the Web3Auth network and the login methods
// here MUST match filedgr-web-app. That parity is what makes the buyer's
// wallet address and Filedgr account identical across the two apps — the
// post-checkout VIEWER grant is issued against exactly that identity
// (SPEC C2). Changing any of it silently grants the wrong account.
import { PRESERVE_ON_LOGOUT } from "@/shared/constants/localStorageKeys";
import {
  Web3AuthProvider as CoreWeb3AuthProvider,
  ResolvedTheme,
  useWeb3Auth,
} from "@filedgr/web-core/auth";
import {
  CHAIN_NAMESPACES,
  WALLET_CONNECTORS,
  WEB3AUTH_NETWORK,
  Web3AuthOptions,
} from "@web3auth/modal";
import React from "react";

export { useWeb3Auth };
export type { Web3AuthContextType } from "@filedgr/web-core/auth";

// Kuebefarm has no theme switcher — the palette is light-only — so nothing
// ever writes this key and the resolved theme is always 'light'. It is kept at
// filedgr-web-app's value so the shared provider behaves identically.
const THEME_STORAGE_KEY = "filedgr-theme";

// `|| ""` only because this repo types `import.meta.env` strictly (SPEC §10);
// filedgr-web-app reads the same variable untyped. An empty client ID makes
// the shared provider's init throw, which it catches — the app still renders,
// login is simply unavailable.
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID || "";
const isProduction = (import.meta.env.VITE_ENV as string) === "production";

const buildOptions = (resolvedTheme: ResolvedTheme): Web3AuthOptions => {
  const chainConfig = {
    chainNamespace: CHAIN_NAMESPACES.EIP155,
    chainId: isProduction
      ? "0x89" // hex of 137, mainnet
      : "0x13882", // hex of 80002, polygon amoy testnet
    rpcTarget: isProduction
      ? import.meta.env.VITE_POLYGON_MAINNET_RPC ||
        "https://patient-attentive-moon.matic.quiknode.pro/e421f30bfdbed3036e4168567a9b21afabd9d77b/"
      : import.meta.env.VITE_POLYGON_AMOY_RPC ||
        "https://withered-hidden-meme.matic-amoy.quiknode.pro/2573d0529c060b351ab3e634c4a1d5c1b1640081/",
    displayName: isProduction ? "Polygon" : "Polygon Amoy Testnet",
    blockExplorerUrl: isProduction
      ? "https://polygonscan.com/"
      : "https://amoy.polygonscan.com/",
    ticker: "POL",
    tickerName: "Polygon Ecosystem Token",
    logo: "https://cryptologos.cc/logos/polygon-matic-logo.png",
  };
  return {
    clientId: clientId,
    web3AuthNetwork: isProduction
      ? WEB3AUTH_NETWORK.SAPPHIRE_MAINNET
      : WEB3AUTH_NETWORK.SAPPHIRE_DEVNET,
    // `useSFAKey` is deliberately NOT set here — filedgr-web-app does not set
    // it either. It is only valid with custom Auth Connections (the default
    // managed ones don't issue SFA keys), and filedgr-web-app dropped both it
    // and the custom connection IDs. Wallet determinism is anchored by the
    // Web3Auth dashboard Auth Connection, not by client code. Never enable it
    // on one app alone: it remaps every user to a different address.
    chains: [chainConfig],
    defaultChainId: chainConfig.chainId,
    uiConfig: {
      appName: "Kuebefarm",
      appUrl: "https://kuebefarm.lu",
      mode: resolvedTheme,
      theme: {
        primary: "#5A6141",
        onPrimary: "#FFFFFF",
      },
      defaultLanguage: "en",
    },
    modalConfig: {
      connectors: {
        [WALLET_CONNECTORS.AUTH]: {
          label: "auth",
          loginMethods: {
            google: {
              name: "Google",
              showOnModal: true,
            },
            apple: { name: "Apple", showOnModal: false },
            twitter: { name: "Twitter", showOnModal: false },
            facebook: { name: "Facebook", showOnModal: false },
            discord: { name: "Discord", showOnModal: false },
            farcaster: { name: "Farcaster", showOnModal: false },
            github: { name: "GitHub", showOnModal: false },
            reddit: { name: "Reddit", showOnModal: false },
            line: { name: "Line", showOnModal: false },
            kakao: { name: "Kakao", showOnModal: false },
            linkedin: { name: "LinkedIn", showOnModal: false },
            twitch: { name: "Twitch", showOnModal: false },
            wechat: { name: "WeChat", showOnModal: false },
            email_passwordless: {
              name: "Email",
              showOnModal: true,
            },
            sms_passwordless: { name: "SMS", showOnModal: false },
          },
        },
      },
    },
  };
};

export const Web3AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <CoreWeb3AuthProvider
    buildOptions={buildOptions}
    themeStorageKey={THEME_STORAGE_KEY}
    // The shared provider clears localStorage on logout. Kuebefarm's own
    // `PRESERVE_ON_LOGOUT` (the chosen locale) has to survive that too, or the
    // language resets every time someone signs out.
    preserveOnLogout={[THEME_STORAGE_KEY, ...PRESERVE_ON_LOGOUT]}
  >
    {children}
  </CoreWeb3AuthProvider>
);
